import { useMemo, useState } from 'react';

import { playerSidebar } from '@/config/sidebarConfig.ts';
import { getTeamsApi } from '@/lib/api/teamsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { PaymentStatus, RefundRequestStatus, TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Team } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import {
  BadgeCheck,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  CreditCard,
  ExternalLink,
  IdCard,
  MapPin,
  Receipt,
  RotateCcw,
  Shield,
  Trophy,
  Users,
} from 'lucide-react';

import DashboardLayout from '../../components/layout/DashboardLayout';
import Badge from '../../components/ui/Badge';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';
import StatCard from '../../components/ui/StatCard';

const FRONT_URL = (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5232';

const GRADIENT_COLORS = [
  'from-orange-500 to-red-600',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
  'from-purple-500 to-violet-600',
];

function getInitials(firstName: string | null, lastName: string | null): string {
  const f = firstName?.[0] ?? '';
  const l = lastName?.[0] ?? '';
  return (f + l).toUpperCase() || '?';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatAmount(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

function RegistrationStatusBadge({ status }: { status: TeamRegistrationStatus }) {
  if (status === TeamRegistrationStatus.CONFIRMED)
    return (
      <Badge variant='success'>
        <BadgeCheck className='w-3 h-3 mr-1 inline' />
        Confirmée
      </Badge>
    );
  if (status === TeamRegistrationStatus.CANCELLED) return <Badge variant='danger'>Annulée</Badge>;
  if (status === TeamRegistrationStatus.REJECTED) return <Badge variant='danger'>Refusée</Badge>;
  return <Badge variant='warning'>En attente</Badge>;
}

function PaymentStatusBadge({
  status,
  registrationStatus,
}: {
  status: PaymentStatus;
  registrationStatus: TeamRegistrationStatus;
}) {
  if (status === PaymentStatus.COMPLETED) return <Badge variant='success'>Payé</Badge>;
  if (status === PaymentStatus.REFUNDED) return <Badge variant='info'>Remboursé</Badge>;
  if (status === PaymentStatus.FAILED) return <Badge variant='danger'>Échoué</Badge>;
  // When registration is rejected/cancelled and never paid, payment is irrelevant
  if (
    (registrationStatus === TeamRegistrationStatus.REJECTED ||
      registrationStatus === TeamRegistrationStatus.CANCELLED) &&
    status === PaymentStatus.PENDING
  )
    return <Badge variant='neutral'>Sans objet</Badge>;
  return <Badge variant='warning'>À payer</Badge>;
}

function CompetitionStatusBadge({ status }: { status: string }) {
  if (status === 'FINISHED') return <Badge variant='info'>Terminé</Badge>;
  if (status === 'IN_PROGRESS') return <Badge variant='info'>En cours</Badge>;
  if (status === 'CANCELLED') return <Badge variant='danger'>Annulé</Badge>;
  return null;
}

function RefundStatusBadge({ team }: { team: Team }) {
  const rr = team.refundRequests?.[0];
  if (!rr) return null;
  if (rr.status === RefundRequestStatus.PENDING)
    return (
      <Badge variant='warning'>
        <RotateCcw className='w-3 h-3 mr-1 inline' />
        Remboursement en cours
      </Badge>
    );
  if (rr.status === RefundRequestStatus.APPROVED)
    return (
      <Badge variant='info'>
        <RotateCcw className='w-3 h-3 mr-1 inline' />
        Remboursement approuvé
      </Badge>
    );
  if (rr.status === RefundRequestStatus.REJECTED)
    return (
      <Badge variant='danger'>
        <RotateCcw className='w-3 h-3 mr-1 inline' />
        Remboursement refusé
      </Badge>
    );
  return null;
}

function InscriptionCard({ team }: { team: Team }) {
  const comp = team.competition;
  const [playersExpanded, setPlayersExpanded] = useState(false);
  const payment = team.payments?.[0];
  const receiptUrl = payment?.stripeReceiptUrl;

  return (
    <div className='bg-white rounded-2xl border border-slate-200 hover:shadow-xl hover:border-orange-300 transition-all overflow-hidden'>
      {/* Header — comp info */}
      <div className='p-5 pb-0'>
        <div className='flex items-start justify-between mb-3'>
          <div className='flex-1'>
            <div className='flex flex-wrap items-center gap-2 mb-2'>
              <RegistrationStatusBadge status={team.registrationStatus} />
              <PaymentStatusBadge status={team.paymentStatus} registrationStatus={team.registrationStatus} />
              {comp?.status && <CompetitionStatusBadge status={comp.status} />}
              <RefundStatusBadge team={team} />
            </div>
            <a
              href={comp?.id ? `${FRONT_URL}/concours/${comp.id}` : '#'}
              target='_blank'
              rel='noopener noreferrer'
              className='group inline-flex items-center gap-1.5'
            >
              <h3 className='font-bold text-slate-900 text-lg group-hover:text-orange-600 transition-colors'>
                {comp?.name ?? `Concours #${team.competitionId}`}
              </h3>
              <ExternalLink className='w-4 h-4 text-slate-400 group-hover:text-orange-500 transition-colors' />
            </a>
            <div className='flex flex-wrap items-center gap-4 mt-1'>
              {comp?.date && (
                <span className='flex items-center gap-1 text-sm text-slate-500'>
                  <Calendar className='w-4 h-4' />
                  {formatDate(comp.date)}
                </span>
              )}
              {(comp?.venue || comp?.city) && (
                <span className='flex items-center gap-1 text-sm text-slate-500'>
                  <MapPin className='w-4 h-4' />
                  {[comp.venue, comp.city].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Équipe — header + toggle */}
      <div className='px-5'>
        <button
          type='button'
          onClick={() => setPlayersExpanded(prev => !prev)}
          className='w-full flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer'
        >
          <div className='flex items-center gap-3'>
            {team.players && team.players.length > 0 ? (
              <div className='flex -space-x-2'>
                {team.players.slice(0, 4).map((p, idx) => (
                  <div
                    key={p.id}
                    title={`${p.firstName} ${p.lastName}`}
                    className={`w-8 h-8 bg-linear-to-br ${GRADIENT_COLORS[idx % GRADIENT_COLORS.length]} rounded-full flex items-center justify-center font-bold text-white text-xs border-2 border-white`}
                  >
                    {getInitials(p.firstName, p.lastName)}
                  </div>
                ))}
              </div>
            ) : (
              <div className='w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center'>
                <Users className='w-4 h-4 text-slate-400' />
              </div>
            )}
            <div className='text-left'>
              <p className='text-sm font-semibold text-slate-900'>{team.name}</p>
              <p className='text-xs text-slate-500'>
                {team.players?.length ?? 0} joueur{(team.players?.length ?? 0) > 1 ? 's' : ''}
                {team.club ? ` · ${team.club.name}` : ''}
              </p>
            </div>
          </div>
          {playersExpanded ? (
            <ChevronUp className='w-4 h-4 text-slate-400' />
          ) : (
            <ChevronDown className='w-4 h-4 text-slate-400' />
          )}
        </button>

        {/* Players detail */}
        {playersExpanded && team.players && team.players.length > 0 && (
          <div className='mt-2 rounded-xl border border-slate-100 divide-y divide-slate-100'>
            {team.players.map((p, idx) => (
              <div key={p.id} className='flex items-center gap-3 px-3 py-2'>
                <div
                  className={`w-7 h-7 bg-linear-to-br ${GRADIENT_COLORS[idx % GRADIENT_COLORS.length]} rounded-full flex items-center justify-center font-bold text-white text-[10px] shrink-0`}
                >
                  {getInitials(p.firstName, p.lastName)}
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-slate-800 truncate'>
                    {p.firstName} {p.lastName}
                    {team.captainId === p.userId && (
                      <span className='ml-1.5 inline-flex items-center gap-0.5 text-xs text-orange-600 font-semibold'>
                        <Shield className='w-3 h-3' />
                        Capitaine
                      </span>
                    )}
                  </p>
                </div>
                {p.licenseNumber && (
                  <span className='flex items-center gap-1 text-xs text-slate-400 shrink-0'>
                    <IdCard className='w-3 h-3' />
                    {p.licenseNumber}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notes */}
      {team.notes && (
        <div className='px-5 mt-3'>
          <div className='p-3 bg-amber-50 border border-amber-200 rounded-xl'>
            <p className='text-xs font-semibold text-amber-700 mb-0.5'>Notes</p>
            <p className='text-sm text-amber-900'>{team.notes}</p>
          </div>
        </div>
      )}

      {/* Footer — paiement + dates */}
      <div className='px-5 mt-3 pb-5'>
        <div className='grid grid-cols-2 sm:grid-cols-4 gap-4 py-3 border-t border-slate-100'>
          <div>
            <p className='text-xs text-slate-500 mb-1'>Inscription</p>
            <p className='font-bold text-slate-900'>{formatShortDate(team.createdAt)}</p>
          </div>
          <div>
            <p className='text-xs text-slate-500 mb-1'>Montant payé</p>
            <p className='font-bold text-slate-900'>{team.paidAmount != null ? formatAmount(team.paidAmount) : '—'}</p>
          </div>
          <div>
            <p className='text-xs text-slate-500 mb-1'>Joueurs</p>
            <p className='font-bold text-slate-900'>{team.players?.length ?? '—'}</p>
          </div>
          <div>
            <p className='text-xs text-slate-500 mb-1'>Recu</p>
            {receiptUrl ? (
              <a
                href={receiptUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors'
              >
                <Receipt className='w-3.5 h-3.5' />
                Voir
              </a>
            ) : team.paymentStatus === PaymentStatus.COMPLETED ? (
              <p className='text-sm text-slate-400'>Non disponible</p>
            ) : (
              <p className='text-sm text-slate-400'>—</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PlayerInscriptions() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.teams.list({ captainId: user?.id }),
    queryFn: () => getTeamsApi({ captainId: user!.id, limit: 100 }),
    enabled: isAuthenticated && !!user,
  });

  const teams = useMemo<Team[]>(() => data?.data ?? [], [data]);

  const confirmed = useMemo(
    () => teams.filter(t => t.registrationStatus === TeamRegistrationStatus.CONFIRMED),
    [teams],
  );
  const pending = useMemo(() => teams.filter(t => t.registrationStatus === TeamRegistrationStatus.PENDING), [teams]);
  const pastOrFinished = useMemo(() => teams.filter(t => t.competition?.status === 'FINISHED'), [teams]);

  const totalPaid = useMemo(() => teams.reduce((sum, t) => sum + (t.paidAmount ?? 0), 0), [teams]);

  const showSkeleton = isLoading || !isAuthenticated;

  if (isError) {
    return (
      <DashboardLayout sidebarConfig={playerSidebar}>
        <div className='p-8'>
          <QueryErrorState onRetry={refetch} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout sidebarConfig={playerSidebar}>
      <div className='p-8'>
        <PageHeader title='Mes Inscriptions' subtitle='Suivi de vos participations aux concours' />

        {/* Stats */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
          <StatCard
            icon={CheckCircle}
            iconBg='bg-emerald-100'
            iconColor='text-emerald-600'
            label='Confirmées'
            value={showSkeleton ? '—' : String(confirmed.length)}
            subtitle={`${pending.length} en attente`}
          />
          <StatCard
            icon={Trophy}
            iconBg='bg-blue-100'
            iconColor='text-blue-600'
            label='Terminées'
            value={showSkeleton ? '—' : String(pastOrFinished.length)}
            subtitle='Concours passés'
          />
          <StatCard
            icon={Users}
            iconBg='bg-purple-100'
            iconColor='text-purple-600'
            label='Total inscriptions'
            value={showSkeleton ? '—' : String(teams.length)}
            subtitle='Depuis la création du compte'
          />
          <StatCard
            icon={CreditCard}
            iconBg='bg-amber-100'
            iconColor='text-amber-600'
            label='Total payé'
            value={showSkeleton ? '—' : formatAmount(totalPaid)}
            subtitle={`${teams.filter(t => t.paymentStatus === PaymentStatus.COMPLETED).length} paiement${teams.filter(t => t.paymentStatus === PaymentStatus.COMPLETED).length > 1 ? 's' : ''}`}
          />
        </div>

        {/* Liste */}
        {showSkeleton ? (
          <div className='space-y-4'>
            {[1, 2, 3].map(i => (
              <div key={i} className='h-48 animate-pulse rounded-2xl bg-slate-200' />
            ))}
          </div>
        ) : teams.length === 0 ? (
          <div className='bg-white rounded-2xl border border-slate-200 p-12 text-center'>
            <div className='w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4'>
              <Clock className='w-8 h-8 text-slate-400' />
            </div>
            <h3 className='font-bold text-slate-900 text-lg mb-2'>Aucune inscription</h3>
            <p className='text-slate-500'>Vous n&apos;êtes encore inscrit à aucun concours.</p>
          </div>
        ) : (
          <div className='space-y-4'>
            {teams.map(team => (
              <InscriptionCard key={team.id} team={team} />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
